import theme from '@cubeyou-team/hx-theme';
import { SsoProvider } from '@discover-frontend/auth';
import '@discover-frontend/auth/dist/auth.css';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect, Provider } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import 'typeface-muli';

import GlobalStyles from '../../global-styles';
import GenericErrorContainer from '../GenericErrorContainer';
import InitContainer from '../InitContainer';
import ModalRootContainer from '../ModalRootContainer';
import ToastRootContainer from '../ToastRootContainer';

import { MinResolutionScreen } from '../../components';

import routes from '../../paths';
import { ROUTES } from '../../constants/routes';
import { NotFound, PrivateRoute } from '../../routes';

import { AppShell } from './components/app-shell';
import { initialAuth } from '../../actions/AuthActions';

import { accountsSsoConfig } from '../../constants/ssoConfig';
import { LanguageContext } from '../../contexts';
import { ReactQueryProvider } from '../../providers/ReactQueryProvider';
import { TrialLockoutProvider } from '../../modules/free-trial/providers/TrialLockoutProvider';

const supportsHistory = 'pushState' in window.history;

class Root extends Component {
  renderAppContent() {
    const { auth } = this.props;

    return (
      <BrowserRouter basename={'/'} forceRefresh={!supportsHistory}>
        <>
          <InitContainer>
            <AppShell auth={auth} />
            <Switch>
              {routes.map((route, index) =>
                route.private ? (
                  <PrivateRoute
                    key={`route-${index}`}
                    exact={route.exact}
                    path={route.path}
                    auth={auth}
                    component={(props) => (
                      <route.component {...props} subpaths={route.routes} />
                    )}
                    redirectPath={route.redirectPath}
                  />
                ) : (
                  <Route
                    key={`route-${index}`}
                    path={route.path}
                    component={route.component}
                  />
                )
              )}
              <Route
                key={`route-404`}
                path={'/not-found'}
                component={NotFound}
              />
              <Redirect from="*" to={ROUTES.LOGIN} />
            </Switch>
            {auth.isLogged && <ModalRootContainer />}
            {auth.isLogged && <MinResolutionScreen />}
            <GenericErrorContainer />
          </InitContainer>
          {/* Outside InitContainer so trial lockout / bootstrap holds still show toasts. */}
          {auth.isLogged && <ToastRootContainer />}
        </>
      </BrowserRouter>
    );
  }

  render() {
    const { store, auth } = this.props;

    let scriptHotjar = document.createElement('script');
    const hotjar = `;(function(h, o, t, j, a, r) {
        h.hj =
          h.hj ||
          function() {
            ;(h.hj.q = h.hj.q || []).push(arguments)
          }
        h._hjSettings = { hjid: 3780531, hjsv: 6 }
        a = o.getElementsByTagName('head')[0]
        r = o.createElement('script')
        r.async = 1
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv
        a.appendChild(r)
      })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=')`;

    scriptHotjar.innerHTML = hotjar;
    const head = document.getElementsByTagName('head')[0];
    if (process.env.REACT_APP_API_ENV === 'production') {
      head.appendChild(scriptHotjar);
    }

    const appContent = this.renderAppContent();

    return (
      <LanguageContext.Provider value={'en-US'}>
        <Provider store={store}>
          <ReactQueryProvider>
            <ThemeProvider theme={theme}>
              <TrialLockoutProvider decodedToken={auth.decodedToken}>
                <GlobalStyles />
                {auth.isLogged ? (
                  <SsoProvider config={accountsSsoConfig}>
                    {appContent}
                  </SsoProvider>
                ) : (
                  appContent
                )}
              </TrialLockoutProvider>
            </ThemeProvider>
          </ReactQueryProvider>
        </Provider>
      </LanguageContext.Provider>
    );
  }
}

const mapDispatchToProps = {
  initialAuth,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

Root.propTypes = {
  store: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Root);
